import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Save01, X, ChevronRight, ChevronLeft } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { TextArea } from '@/components/base/textarea/textarea';
import { RadioGroup, RadioButton } from '@/components/base/radio-buttons/radio-buttons';
import { MultiSelectBase } from '@/components/base/select/multi-select';
import { SelectItem } from '@/components/base/select/select-item';
import { Stepper, StepperStep } from '@/components/application/stepper';
import { accountService } from '@/data/mock-accounts';
import { countries } from '@/data/countries';
import type {
    CreateAccountData,
    UpdateAccountData,
    EntityType,
    AccountStatus,
    UserType,
    CommodityType,
    AccountFormState,
    AccountFormStep1Data,
    AccountFormStep2Data
} from '@/types/account';
import { useTranslation } from 'react-i18next';
import { useListData } from 'react-stately';
import type { Key } from 'react-aria-components';

export const AccountFormPage = () => {
    const { t } = useTranslation(['pages:accounts', 'common']);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);

    const [formState, setFormState] = useState<AccountFormState>({
        currentStep: 1,
        isStepValid: { 1: false, 2: false },
        step1: {
            userType: 'SHIPPER' as UserType,
            legalName: '',
            entityType: '' as EntityType | '',
            taxId: '',
            address: {
                street: '',
                city: '',
                state: '',
                postalCode: ''
            },
            country: '',
            email: '',
            phone: '',
            status: 'Attivo' as AccountStatus,
            notes: ''
        },
        step2: {
            commodityType: '' as CommodityType | '',
            specificProducts: '',
            mainOrigins: [],
            mainDestinations: [],
            monthlyContainerVolume: '' as number | ''
        }
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Multi-select state management
    const selectedOrigins = useListData({
        initialItems: []
    });

    const selectedDestinations = useListData({
        initialItems: []
    });

    const entityTypeOptions = [
        { id: 'LLC', label: 'LLC' },
        { id: 'Corporation', label: 'Corporation' },
        { id: 'Partnership', label: 'Partnership' },
        { id: 'Sole Proprietorship', label: 'Sole Proprietorship' },
        { id: 'NGO', label: 'NGO' }
    ];

    const statusOptions = [
        { id: 'Attivo', label: t('list.status.active') },
        { id: 'Sospeso', label: t('list.status.suspended') },
        { id: 'Inattivo', label: t('list.status.inactive') }
    ];

    const userTypeOptions = [
        { id: 'SHIPPER', label: 'SHIPPER' },
        { id: 'RECEIVER', label: 'RECEIVER' }
    ];

    const countryOptions = countries.map(country => ({
        id: country.id,
        label: country.label
    }));

    const commodityTypeOptions = [
        {
            id: 'fresh' as CommodityType,
            label: t('form.fields.commodityTypeOptions.fresh.label'),
            hint: t('form.fields.commodityTypeOptions.fresh.description')
        },
        {
            id: 'soft' as CommodityType,
            label: t('form.fields.commodityTypeOptions.soft.label'),
            hint: t('form.fields.commodityTypeOptions.soft.description')
        },
        {
            id: 'hard' as CommodityType,
            label: t('form.fields.commodityTypeOptions.hard.label'),
            hint: t('form.fields.commodityTypeOptions.hard.description')
        }
    ];

    // Validation functions
    const validateStep1 = (data: AccountFormStep1Data): boolean => {
        return !!(
            data.userType &&
            data.legalName.trim() &&
            data.entityType &&
            data.taxId.trim() &&
            data.address.street.trim() &&
            data.address.city.trim() &&
            data.address.state.trim() &&
            data.address.postalCode.trim() &&
            data.country &&
            data.email.trim() &&
            data.status
        );
    };

    const validateStep2 = (data: AccountFormStep2Data): boolean => {
        return !!(
            data.commodityType &&
            data.mainOrigins.length > 0 &&
            data.mainDestinations.length > 0 &&
            data.monthlyContainerVolume !== ''
        );
    };

    useEffect(() => {
        if (isEdit && id) {
            const loadAccount = async () => {
                setIsLoading(true);

                // Simulate API call
                await new Promise(resolve => setTimeout(resolve, 500));

                const account = accountService.getById(id);
                if (account) {
                    const newFormState: AccountFormState = {
                        currentStep: 1,
                        isStepValid: { 1: false, 2: false },
                        step1: {
                            userType: account.userType,
                            legalName: account.legalName,
                            entityType: account.entityType,
                            taxId: account.taxId,
                            address: account.address,
                            country: account.country,
                            email: account.email,
                            phone: account.phone || '',
                            status: account.status,
                            notes: account.notes || ''
                        },
                        step2: {
                            commodityType: account.tradeDetails?.commodityType || '',
                            specificProducts: account.tradeDetails?.specificProducts || '',
                            mainOrigins: account.tradeDetails?.mainOrigins || [],
                            mainDestinations: account.tradeDetails?.mainDestinations || [],
                            monthlyContainerVolume: account.tradeDetails?.monthlyContainerVolume || ''
                        }
                    };

                    // Validate steps
                    newFormState.isStepValid[1] = validateStep1(newFormState.step1);
                    newFormState.isStepValid[2] = validateStep2(newFormState.step2);

                    setFormState(newFormState);

                    // Update multi-select states
                    if (account.tradeDetails?.mainOrigins) {
                        const originItems = account.tradeDetails.mainOrigins.map(countryId => {
                            const country = countries.find(c => c.id === countryId);
                            return country ? { id: country.id, label: country.label } : null;
                        }).filter(Boolean);
                        selectedOrigins.setSelectedKeys(new Set(originItems.map(item => item!.id)));
                    }

                    if (account.tradeDetails?.mainDestinations) {
                        const destinationItems = account.tradeDetails.mainDestinations.map(countryId => {
                            const country = countries.find(c => c.id === countryId);
                            return country ? { id: country.id, label: country.label } : null;
                        }).filter(Boolean);
                        selectedDestinations.setSelectedKeys(new Set(destinationItems.map(item => item!.id)));
                    }
                }
                setIsLoading(false);
            };

            loadAccount();
        }
    }, [isEdit, id]);

    // Update validation when form data changes
    useEffect(() => {
        setFormState(prev => ({
            ...prev,
            isStepValid: {
                1: validateStep1(prev.step1),
                2: validateStep2(prev.step2)
            }
        }));
    }, [formState.step1, formState.step2]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Only allow submit if both steps are valid
        if (!formState.isStepValid[1] || !formState.isStepValid[2]) {
            return;
        }

        setIsSaving(true);

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            const tradeDetails = {
                commodityType: formState.step2.commodityType as CommodityType,
                specificProducts: formState.step2.specificProducts,
                mainOrigins: formState.step2.mainOrigins,
                mainDestinations: formState.step2.mainDestinations,
                monthlyContainerVolume: formState.step2.monthlyContainerVolume as number
            };

            if (isEdit && id) {
                const updateData: UpdateAccountData = {
                    id,
                    ...formState.step1,
                    entityType: formState.step1.entityType as EntityType,
                    address: formState.step1.address,
                    tradeDetails
                };
                accountService.update(id, updateData);
            } else {
                const createData: CreateAccountData = {
                    ...formState.step1,
                    entityType: formState.step1.entityType as EntityType,
                    address: formState.step1.address,
                    password: 'temp-password', // This should be handled properly in real implementation
                    tradeDetails
                };
                accountService.create(createData);
            }

            navigate('/admin/accounts');
        } catch (error) {
            console.error('Error saving account:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/accounts');
    };

    // Stepper navigation
    const handleNextStep = () => {
        if (formState.currentStep === 1 && formState.isStepValid[1]) {
            setFormState(prev => ({ ...prev, currentStep: 2 }));
        }
    };

    const handlePreviousStep = () => {
        if (formState.currentStep === 2) {
            setFormState(prev => ({ ...prev, currentStep: 1 }));
        }
    };

    const handleStepChange = (step: number) => {
        setFormState(prev => ({ ...prev, currentStep: step }));
    };

    // Step 1 handlers
    const updateStep1 = (updates: Partial<AccountFormStep1Data>) => {
        setFormState(prev => ({
            ...prev,
            step1: { ...prev.step1, ...updates }
        }));
    };

    const updateStep1Address = (updates: Partial<AccountFormStep1Data['address']>) => {
        setFormState(prev => ({
            ...prev,
            step1: {
                ...prev.step1,
                address: { ...prev.step1.address, ...updates }
            }
        }));
    };

    // Step 2 handlers
    const updateStep2 = (updates: Partial<AccountFormStep2Data>) => {
        setFormState(prev => ({
            ...prev,
            step2: { ...prev.step2, ...updates }
        }));
    };

    const handleOriginSelectionChange = (key: Key) => {
        const countryId = key.toString();
        const country = countries.find(c => c.id === countryId);
        if (country && !formState.step2.mainOrigins.includes(countryId)) {
            updateStep2({
                mainOrigins: [...formState.step2.mainOrigins, countryId]
            });
        }
    };

    const handleDestinationSelectionChange = (key: Key) => {
        const countryId = key.toString();
        const country = countries.find(c => c.id === countryId);
        if (country && !formState.step2.mainDestinations.includes(countryId)) {
            updateStep2({
                mainDestinations: [...formState.step2.mainDestinations, countryId]
            });
        }
    };

    const handleOriginRemove = (key: Key) => {
        const countryId = key.toString();
        updateStep2({
            mainOrigins: formState.step2.mainOrigins.filter(id => id !== countryId)
        });
    };

    const handleDestinationRemove = (key: Key) => {
        const countryId = key.toString();
        updateStep2({
            mainDestinations: formState.step2.mainDestinations.filter(id => id !== countryId)
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('form.loading')}</p>
                </div>
            </div>
        );
    }

    const stepConfigs = [
        {
            title: t('form.stepper.step1.title'),
            description: t('form.stepper.step1.description')
        },
        {
            title: t('form.stepper.step2.title'),
            description: t('form.stepper.step2.description')
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={handleCancel}
                        size="sm"
                        color="secondary"
                        iconLeading={ArrowLeft}
                    >
                        {t('form.back')}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">
                            {isEdit ? t('form.edit.title') : t('form.create.title')}
                        </h1>
                        <p className="text-tertiary mt-1">
                            {isEdit ? t('form.edit.subtitle') : t('form.create.subtitle')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Stepper Form */}
            <form onSubmit={handleSubmit}>
                <Stepper
                    currentStep={formState.currentStep}
                    steps={stepConfigs}
                    completedSteps={[]}
                    validSteps={Object.keys(formState.isStepValid).filter(step => formState.isStepValid[parseInt(step)]).map(Number)}
                    onStepChange={handleStepChange}
                    allowStepNavigation={true}
                >
                    {/* Step 1: Company Info */}
                    <StepperStep
                        stepNumber={1}
                        title={t('form.stepper.step1.title')}
                        isActive={formState.currentStep === 1}
                        isCompleted={formState.isStepValid[1]}
                        isValid={formState.isStepValid[1]}
                    >
                        <div className="space-y-6">
                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.general')}</h2>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Select
                                        label={t('form.fields.userType')}
                                        placeholder={t('form.fields.userTypePlaceholder')}
                                        items={userTypeOptions}
                                        selectedKey={formState.step1.userType}
                                        onSelectionChange={(key) => updateStep1({ userType: key as UserType })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>

                                    <Input
                                        label={t('form.fields.legalName')}
                                        placeholder={t('form.fields.legalNamePlaceholder')}
                                        value={formState.step1.legalName}
                                        onChange={(value) => updateStep1({ legalName: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Select
                                        label={t('form.fields.entityType')}
                                        placeholder={t('form.fields.entityTypePlaceholder')}
                                        items={entityTypeOptions}
                                        selectedKey={formState.step1.entityType}
                                        onSelectionChange={(key) => updateStep1({ entityType: key as EntityType })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>

                                    <Input
                                        label={t('form.fields.taxId')}
                                        placeholder={t('form.fields.taxIdPlaceholder')}
                                        value={formState.step1.taxId}
                                        onChange={(value) => updateStep1({ taxId: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Select
                                        label={t('form.fields.status')}
                                        placeholder={t('form.fields.statusPlaceholder')}
                                        items={statusOptions}
                                        selectedKey={formState.step1.status}
                                        onSelectionChange={(key) => updateStep1({ status: key as AccountStatus })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>
                            </div>

                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.address')}</h2>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div className="sm:col-span-2">
                                        <Input
                                            label={t('form.fields.street')}
                                            placeholder={t('form.fields.streetPlaceholder')}
                                            value={formState.step1.address.street}
                                            onChange={(value) => updateStep1Address({ street: value })}
                                            isRequired
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('form.fields.city')}
                                        placeholder={t('form.fields.cityPlaceholder')}
                                        value={formState.step1.address.city}
                                        onChange={(value) => updateStep1Address({ city: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.state')}
                                        placeholder={t('form.fields.statePlaceholder')}
                                        value={formState.step1.address.state}
                                        onChange={(value) => updateStep1Address({ state: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.postalCode')}
                                        placeholder={t('form.fields.postalCodePlaceholder')}
                                        value={formState.step1.address.postalCode}
                                        onChange={(value) => updateStep1Address({ postalCode: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Select
                                        label={t('form.fields.country')}
                                        placeholder={t('form.fields.countryPlaceholder')}
                                        items={countryOptions}
                                        selectedKey={formState.step1.country}
                                        onSelectionChange={(key) => updateStep1({ country: key as string })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>
                            </div>

                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.contacts')}</h2>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Input
                                        label={t('form.fields.email')}
                                        type="email"
                                        placeholder={t('form.fields.emailPlaceholder')}
                                        value={formState.step1.email}
                                        onChange={(value) => updateStep1({ email: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.phone')}
                                        placeholder={t('form.fields.phonePlaceholder')}
                                        value={formState.step1.phone}
                                        onChange={(value) => updateStep1({ phone: value })}
                                        size="md"
                                    />
                                </div>
                            </div>

                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.notes')}</h2>

                                <TextArea
                                    label={t('form.fields.notes')}
                                    placeholder={t('form.fields.notesPlaceholder')}
                                    value={formState.step1.notes}
                                    onChange={(value) => updateStep1({ notes: value })}
                                    rows={4}
                                />
                            </div>
                        </div>
                    </StepperStep>

                    {/* Step 2: Trade Details */}
                    <StepperStep
                        stepNumber={2}
                        title={t('form.stepper.step2.title')}
                        description={t('form.stepper.step2.description')}
                        isActive={formState.currentStep === 2}
                        isCompleted={formState.isStepValid[2]}
                        isValid={formState.isStepValid[2]}
                    >
                        <div className="space-y-6">
                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.stepper.step2.title')}</h2>

                                <div className="space-y-6">
                                    {/* Commodity Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-secondary mb-3">
                                            {t('form.fields.commodityType')} *
                                        </label>
                                        <RadioGroup
                                            value={formState.step2.commodityType}
                                            onChange={(value) => updateStep2({ commodityType: value as CommodityType })}
                                            size="md"
                                        >
                                            {commodityTypeOptions.map((option) => (
                                                <RadioButton
                                                    key={option.id}
                                                    value={option.id}
                                                    label={option.label}
                                                    hint={option.hint}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </div>

                                    {/* Specific Products */}
                                    <Input
                                        label={t('form.fields.specificProducts')}
                                        placeholder={t('form.fields.specificProductsPlaceholder')}
                                        value={formState.step2.specificProducts}
                                        onChange={(value) => updateStep2({ specificProducts: value })}
                                        size="md"
                                    />

                                    {/* Main Origins */}
                                    <div>
                                        <label className="block text-sm font-medium text-secondary mb-3">
                                            {t('form.fields.mainOrigins')} *
                                        </label>
                                        <MultiSelectBase
                                            items={countryOptions}
                                            selectedItems={selectedOrigins}
                                            onItemInserted={handleOriginSelectionChange}
                                            onItemCleared={handleOriginRemove}
                                            placeholder={t('form.fields.mainOriginsPlaceholder')}
                                            size="md"
                                        >
                                            {(item) => (
                                                <SelectItem key={item.id} id={item.id}>
                                                    {item.label}
                                                </SelectItem>
                                            )}
                                        </MultiSelectBase>
                                    </div>

                                    {/* Main Destinations */}
                                    <div>
                                        <label className="block text-sm font-medium text-secondary mb-3">
                                            {t('form.fields.mainDestinations')} *
                                        </label>
                                        <MultiSelectBase
                                            items={countryOptions}
                                            selectedItems={selectedDestinations}
                                            onItemInserted={handleDestinationSelectionChange}
                                            onItemCleared={handleDestinationRemove}
                                            placeholder={t('form.fields.mainDestinationsPlaceholder')}
                                            size="md"
                                        >
                                            {(item) => (
                                                <SelectItem key={item.id} id={item.id}>
                                                    {item.label}
                                                </SelectItem>
                                            )}
                                        </MultiSelectBase>
                                    </div>

                                    {/* Monthly Container Volume */}
                                    <Input
                                        label={t('form.fields.monthlyContainerVolume')}
                                        placeholder={t('form.fields.monthlyContainerVolumePlaceholder')}
                                        type="number"
                                        value={formState.step2.monthlyContainerVolume.toString()}
                                        onChange={(value) => updateStep2({ monthlyContainerVolume: value ? parseInt(value) : '' })}
                                        isRequired
                                        size="md"
                                    />
                                </div>
                            </div>
                        </div>
                    </StepperStep>
                </Stepper>

                {/* Form Actions */}
                <div className="flex items-center justify-between mt-8">
                    <div>
                        {formState.currentStep > 1 && (
                            <Button
                                type="button"
                                onClick={handlePreviousStep}
                                size="md"
                                color="secondary"
                                iconLeading={ChevronLeft}
                            >
                                {t('form.actions.previous')}
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-4">
                        <Button
                            type="button"
                            onClick={handleCancel}
                            size="md"
                            color="secondary"
                            iconLeading={X}
                        >
                            {t('form.actions.cancel')}
                        </Button>

                        {formState.currentStep < 2 ? (
                            <Button
                                type="button"
                                onClick={handleNextStep}
                                size="md"
                                color="primary"
                                iconTrailing={ChevronRight}
                                isDisabled={!formState.isStepValid[formState.currentStep]}
                            >
                                {t('form.actions.next')}
                            </Button>
                        ) : (
                            <Button
                                type="submit"
                                size="md"
                                color="primary"
                                iconLeading={Save01}
                                isLoading={isSaving}
                                isDisabled={!formState.isStepValid[1] || !formState.isStepValid[2]}
                            >
                                {isEdit ? t('form.actions.save') : t('form.actions.create')}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};
